import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Visually matched to libs/ui/shared/src/Button.module.scss. Font sizes are set
 * here rather than borrowed from a title step, because two utilities both
 * setting font-size resolve by whichever Tailwind emits last. `not-disabled`
 * rather than `enabled`: ButtonLink shares this recipe, and an anchor never
 * matches :enabled.
 */
export const button = cva(
  [
    'inline-flex cursor-pointer items-center justify-center gap-3 whitespace-nowrap',
    'font-title font-semibold',
    'rounded-full border transition-all duration-200 ease-out',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-periwinkle',
    'disabled:cursor-not-allowed disabled:border-transparent disabled:bg-slate-600 disabled:text-white',
  ],
  {
    variants: {
      intent: {
        /* The hue comes from `tone` below, so the recipe's own output carries one hue. */
        primary: 'text-white',
        secondary: 'border-slate-200 bg-white text-slate-800 shadow-lg not-disabled:hover:border-periwinkle',
        /* No text colour, so these stay legible on a dark band too. */
        outline: 'border-current bg-transparent not-disabled:hover:opacity-70',
        ghost: 'border-transparent bg-transparent not-disabled:hover:opacity-70',
      },
      size: {
        /* `sm` takes title(7)'s mobile 0.875rem; 1rem in a 32px pill reads cramped. */
        sm: 'h-8 px-4 text-sm',
        /* A step down under lg, as libs/ui/shared's Button steps its medium
           from 48px to 42px under $device-md: the type around it is on its
           mobile step there, and a 48px pill of 18px text reads a size too
           big beside it. lg, not 71rem, because that is where the Input steps
           too.

           The desktop values are the base and the phone's ride on max-lg, so
           a call site that pins a padding or a height keeps winning on a
           desktop. One that wants to pin the phone too says max-lg: as well,
           or the phone step lands under its pin: a size-8 p-0 over md takes
           40px of padding on a phone and squeezes its icon out. A pin over
           sm, which has one size at every width, holds as written. */
        md: 'h-12 px-6 text-lg max-lg:h-11 max-lg:px-5 max-lg:text-base',
        lg: 'h-14 px-8 text-2xl max-lg:h-12 max-lg:px-6 max-lg:text-lg',
      },
      /** The hover lift. */
      float: {
        true: 'not-disabled:motion-safe:hover:-translate-y-1.5',
        false: '',
      },
      block: {
        true: 'w-full',
      },
      /**
       * A primary button's hue: its edge, ground, shadow and hover together,
       * so no call site re-derives the four from a colour. Orange is the
       * brand's and the default; the others are the hues with a shadow token.
       * Only `primary` reads it, through the compound variants: a hue in the
       * intent as well would leave two in the raw string for `cn` to settle.
       */
      tone: {
        orange: '',
        periwinkle: '',
        scarlet: '',
      },
    },
    compoundVariants: [
      {
        intent: 'primary',
        tone: 'orange',
        class: 'border-orange-700/25 bg-orange shadow-md-orange not-disabled:hover:bg-orange-700',
      },
      {
        intent: 'primary',
        tone: 'periwinkle',
        class: 'border-periwinkle-700/25 bg-periwinkle shadow-md-periwinkle not-disabled:hover:bg-periwinkle-700',
      },
      {
        intent: 'primary',
        tone: 'scarlet',
        class: 'border-scarlet-700/25 bg-scarlet shadow-md-scarlet not-disabled:hover:bg-scarlet-700',
      },
    ],
    defaultVariants: {
      intent: 'primary',
      size: 'md',
      float: true,
      tone: 'orange',
    },
  },
);

export type ButtonVariants = VariantProps<typeof button>;
